export const PRODUCT_UNDO_DURATION_MS = 10_000

export interface ProductUndoAction {
  id: string
  message: string
  undo: () => Promise<boolean | void> | boolean | void
  successMessage?: string
  errorMessage?: string
}

interface ProductUndoCoordinatorOptions {
  onExpire?: (action: ProductUndoAction) => void
  schedule?: (callback: () => void, delayMs: number) => ProductUndoTimer
  cancelScheduled?: (timeoutId: ProductUndoTimer) => void
}

type ProductUndoTimer = number | NodeJS.Timeout

export interface ProductUndoCoordinator {
  clear: (actionId?: string) => boolean
  offer: (action: ProductUndoAction, durationMs?: number) => void
  peek: () => ProductUndoAction | null
  take: () => ProductUndoAction | null
}

export interface UndoableCommitOptions<T> {
  commit: () => Promise<T>
  restore: (committedValue: T) => Promise<T>
  onCommit: (committedValue: T) => void
  onCommitError: (error: unknown) => void
  onRestoreStart: () => void
  onRestore: (restoredValue: T) => void
  onRestoreError: (committedValue: T, error: unknown) => Promise<void> | void
}

interface UndoableCommit<T> {
  settled: Promise<T>
  undo: () => Promise<boolean>
}

export const startUndoableCommit = <T>(options: UndoableCommitOptions<T>): UndoableCommit<T> => {
  let undoRequested = false
  const settled = options.commit().then(
    (committedValue) => {
      if (!undoRequested) options.onCommit(committedValue)
      return committedValue
    },
    (error: unknown) => {
      options.onCommitError(error)
      throw error
    },
  )

  const undo = async (): Promise<boolean> => {
    undoRequested = true
    let committedValue: T
    try {
      committedValue = await settled
    } catch {
      return false
    }

    options.onRestoreStart()
    try {
      const restoredValue = await options.restore(committedValue)
      options.onRestore(restoredValue)
      return true
    } catch (error) {
      await options.onRestoreError(committedValue, error)
      throw error
    }
  }

  return { settled, undo }
}

export const createProductUndoCoordinator = (options: ProductUndoCoordinatorOptions = {}): ProductUndoCoordinator => {
  const schedule = options.schedule ?? setTimeout
  const cancelScheduled = options.cancelScheduled ?? clearTimeout
  let activeAction: ProductUndoAction | null = null
  let expiration: ProductUndoTimer | null = null

  const clearExpiration = () => {
    if (expiration === null) return
    cancelScheduled(expiration)
    expiration = null
  }

  const clear = (actionId?: string): boolean => {
    if (!activeAction || (actionId && activeAction.id !== actionId)) return false
    clearExpiration()
    activeAction = null
    return true
  }

  const offer = (action: ProductUndoAction, durationMs = PRODUCT_UNDO_DURATION_MS) => {
    clearExpiration()
    activeAction = action
    expiration = schedule(() => {
      if (activeAction?.id !== action.id) return
      activeAction = null
      expiration = null
      options.onExpire?.(action)
    }, durationMs)
  }

  const take = (): ProductUndoAction | null => {
    const action = activeAction
    if (!action) return null
    clear(action.id)
    return action
  }

  return {
    clear,
    offer,
    peek: () => activeAction,
    take,
  }
}

interface ProductUndoKeyboardEvent {
  altKey: boolean
  ctrlKey: boolean
  defaultPrevented: boolean
  key: string
  metaKey: boolean
  shiftKey: boolean
  target: EventTarget | null
}

const isEditableTarget = (target: EventTarget | null): boolean => {
  if (typeof HTMLElement === "undefined" || !(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  return target.matches("input, textarea, select, [contenteditable='true']")
}

export const shouldHandleProductUndoHotkey = (event: ProductUndoKeyboardEvent): boolean => {
  if (event.defaultPrevented || event.altKey || event.shiftKey) return false
  if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== "z") return false
  return !isEditableTarget(event.target)
}

export const getProductUndoHotkeyLabel = (): string => {
  if (typeof navigator === "undefined") return "Ctrl+Z"
  return /Mac|iPhone|iPad|iPod/i.test(navigator.platform) ? "⌘Z" : "Ctrl+Z"
}
